import React, { useCallback, useEffect, useMemo, useState } from "react";
import ApiData from "../../../api/ApiData";
import { BtnPrimaryv4 } from "../../UI/button/BtnPrimaryv4";
import { BtnOutlineV4 } from "../../UI/button/BtnOutlineV4";
import { formatMoneyString } from "../../../utils/integration1cPriceDisplay";
import {
  defaultInvoiceDateRange,
  filterInvoicesBySearch,
  formatInvoiceDate,
  INVOICES_PAGE_SIZE,
  sortInvoices,
} from "../../../utils/companyInvoicesUtils";
import { CompanyInvoiceLinesDrawer } from "./CompanyInvoiceLinesDrawer";

const GRID_CLASS = "cc-v4-soft-grid company-invoices-1c-grid";
const TAB_NAME_1C = "1с";

export { TAB_NAME_1C };

const ROW_CLASS = `cc-v4-soft-row reports-soft-row ${GRID_CLASS} company-invoices-1c-row`;
const HEADER_CLASS = `lic_container_header cc-v4-soft-header ${GRID_CLASS}`;

function InvoicePager({ page, totalPages, onPageChange }) {
  if (totalPages <= 1) {
    return null;
  }
  return (
    <div className="company-invoices-pager">
      <span className="company-invoices-pager-label">
        Страница {page} из {totalPages}
      </span>
      <BtnOutlineV4
        text="Назад"
        disabled={page <= 1}
        onClick={() => onPageChange(page - 1)}
      />
      <BtnOutlineV4
        text="Вперёд"
        disabled={page >= totalPages}
        onClick={() => onPageChange(page + 1)}
      />
    </div>
  );
}

function InvoicesListSkeleton() {
  const skeletonRow = `${ROW_CLASS} company-invoices-skeleton-row`;
  return (
    <div
      className="cc-v4-soft-body"
      aria-busy="true"
      aria-label="Загрузка заказов"
    >
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className={skeletonRow}>
          <span
            className={`company-invoices-skeleton-cell company-invoices-col-order${
              i % 2 === 1 ? " company-invoices-skeleton-cell--short" : ""
            }`}
          />
          <span
            className="company-invoices-skeleton-cell company-invoices-skeleton-cell--numeric company-invoices-col-amount"
          />
          <span className="company-invoices-skeleton-cell company-invoices-col-tax" />
          <span
            className={`company-invoices-skeleton-cell company-invoices-col-created${
              i % 3 === 0 ? " company-invoices-skeleton-cell--short" : ""
            }`}
          />
          <span className="company-invoices-skeleton-cell company-invoices-col-closed" />
        </div>
      ))}
    </div>
  );
}

function InvoicesTableHeader({ searchQuery, setSearchQuery, toggleSort }) {
  return (
    <div className={HEADER_CLASS}>
      <label
        className="cc-v4-soft-th company-invoices-col-order search_id_client_container cc-v4-soft-th-id"
      >
        <span className="cc-v4-soft-th-label">Заказ в 1С</span>
        <input
          className="search_id_client cc-v4-soft-id-input"
          placeholder="Поиск…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          type="text"
          aria-label="Поиск по заказу в 1С"
          onClick={(e) => e.stopPropagation()}
        />
      </label>
      <button
        type="button"
        className="cc-v4-soft-th company-invoices-col-amount"
        onClick={() => toggleSort("amount")}
      >
        <span className="cc-v4-soft-th-label">Сумма</span>
        <span className="cc-v4-sort-icon" aria-hidden="true" />
      </button>
      <span className="cc-v4-soft-th-static company-invoices-col-tax">
        <span className="cc-v4-soft-th-label">НДС %</span>
      </span>
      <button
        type="button"
        className="cc-v4-soft-th company-invoices-col-created"
        onClick={() => toggleSort("begindate")}
      >
        <span className="cc-v4-soft-th-label">Создан заказ</span>
        <span className="cc-v4-sort-icon" aria-hidden="true" />
      </button>
      <button
        type="button"
        className="cc-v4-soft-th company-invoices-col-closed"
        onClick={() => toggleSort("closedate")}
      >
        <span className="cc-v4-soft-th-label">Закрыт заказ</span>
        <span className="cc-v4-sort-icon" aria-hidden="true" />
      </button>
    </div>
  );
}

export function CompanyInvoices1cPanel({ companyId }) {
  const defaults = useMemo(() => defaultInvoiceDateRange(), []);
  const [draftDateFrom, setDraftDateFrom] = useState(defaults.dateFrom);
  const [draftDateTo, setDraftDateTo] = useState(defaults.dateTo);
  const [appliedDateFrom, setAppliedDateFrom] = useState(defaults.dateFrom);
  const [appliedDateTo, setAppliedDateTo] = useState(defaults.dateTo);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortField, setSortField] = useState("begindate");
  const [sortDesc, setSortDesc] = useState(true);
  const [linesDrawerOpen, setLinesDrawerOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const totalPages = Math.max(
    1,
    Math.ceil(totalRecords / INVOICES_PAGE_SIZE),
  );

  const loadInvoices = useCallback(async () => {
    if (!companyId) {
      return;
    }
    setLoading(true);
    setError(false);
    const data = await ApiData.getCompanyInvoices(companyId, {
      date_from: appliedDateFrom,
      date_to: appliedDateTo,
      number: page,
      size: INVOICES_PAGE_SIZE,
    });
    if (!data) {
      setError(true);
      setInvoices([]);
      setTotalRecords(0);
    } else {
      setInvoices(data.invoices ?? []);
      setTotalRecords(data.total_records ?? 0);
    }
    setLoading(false);
  }, [companyId, appliedDateFrom, appliedDateTo, page]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  useEffect(() => {
    setPage(1);
    setDraftDateFrom(defaults.dateFrom);
    setDraftDateTo(defaults.dateTo);
    setAppliedDateFrom(defaults.dateFrom);
    setAppliedDateTo(defaults.dateTo);
    setSearchQuery("");
  }, [companyId, defaults.dateFrom, defaults.dateTo]);

  const applyDateFilter = () => {
    setPage(1);
    setAppliedDateFrom(draftDateFrom);
    setAppliedDateTo(draftDateTo);
  };

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDesc((v) => !v);
    } else {
      setSortField(field);
      setSortDesc(field === "begindate");
    }
  };

  const visibleRows = useMemo(() => {
    const filtered = filterInvoicesBySearch(invoices, searchQuery);
    return sortInvoices(filtered, { field: sortField, desc: sortDesc });
  }, [invoices, searchQuery, sortField, sortDesc]);

  const openLines = (invoice) => {
    setSelectedInvoice(invoice);
    setLinesDrawerOpen(true);
  };

  const closeLines = () => {
    setLinesDrawerOpen(false);
    setSelectedInvoice(null);
  };

  const showPager = !loading && !error;

  return (
    <>
      <div className="filters-bar company-invoices-filters">
        <div className="filter_container company-invoices-date-filter">
          <span>От</span>
          <input
            className="input_date"
            type="date"
            value={draftDateFrom}
            onChange={(e) => setDraftDateFrom(e.target.value)}
          />
          <span>До</span>
          <input
            className="input_date"
            type="date"
            value={draftDateTo}
            onChange={(e) => setDraftDateTo(e.target.value)}
          />
          <BtnPrimaryv4 text="Показать" onClick={applyDateFilter} />
        </div>
      </div>

      <div className="card table-container company-card-v4-soft-table company-invoices-1c-panel">
        <div className="cc-v4-soft-list-root">
          <InvoicesTableHeader
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            toggleSort={toggleSort}
          />

          {loading ? <InvoicesListSkeleton /> : null}

          {!loading && error ? (
            <div className="cc-v4-soft-body">
              <p className="grey_info company-invoices-empty">
                Не удалось загрузить заказы 1С
              </p>
            </div>
          ) : null}

          {!loading && !error && visibleRows.length === 0 ? (
            <div className="cc-v4-soft-body">
              <p className="grey_info company-invoices-empty">
                Заказы не найдены
              </p>
            </div>
          ) : null}

          {!loading && !error && visibleRows.length > 0 ? (
            <div className="cc-v4-soft-body">
              {visibleRows.map((row) => (
                <div
                  key={row.bitrix_id}
                  className={ROW_CLASS}
                  role="button"
                  tabIndex={0}
                  onClick={() => openLines(row)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openLines(row);
                    }
                  }}
                >
                  <span className="reports-cell company-invoices-col-order">
                    {row.invoice_1c_field || "—"}
                  </span>
                  <span
                    className="reports-cell reports-cell-numeric company-invoices-col-amount"
                  >
                    {formatMoneyString(row.amount)}
                  </span>
                  <span className="reports-cell company-invoices-col-tax">
                    {row.tax_value ?? "—"}
                  </span>
                  <span className="reports-cell company-invoices-col-created">
                    {formatInvoiceDate(row.begindate)}
                  </span>
                  <span className="reports-cell company-invoices-col-closed">
                    {formatInvoiceDate(row.closedate)}
                  </span>
                </div>
              ))}
            </div>
          ) : null}
        </div>

        {showPager ? (
          <InvoicePager
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        ) : null}
      </div>

      <CompanyInvoiceLinesDrawer
        open={linesDrawerOpen}
        onClose={closeLines}
        bitrixId={selectedInvoice?.bitrix_id}
        invoiceTitle={selectedInvoice?.invoice_1c_field}
      />
    </>
  );
}
